import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import connectionService from '../../../../services/community/connectionService';
import { RootState } from '../../../../redux/reducers';
import { Connection, ConnectionType } from '../../../../redux/slices/connectionsSlice';

export type DraftShiftMemberAssignee = {
  key: string;
  type: ConnectionType;
  label: string;
  userId?: number;
  externalMemberId?: number;
};

function getConnectionLabel(connection: Connection): string {
  return `${connection.fields.FIRST_NAME || ''} ${connection.fields.LAST_NAME || ''}`.trim();
}

function mapConnectionToAssignee(connection: Connection): DraftShiftMemberAssignee | null {
  if (connection.type === ConnectionType.MEMBER && connection.userId) {
    return {
      key: `member-${connection.userId}`,
      type: ConnectionType.MEMBER,
      label: getConnectionLabel(connection),
      userId: connection.userId,
    };
  }

  if (connection.type === ConnectionType.EXTERNAL_MEMBER && connection.externalMemberId) {
    return {
      key: `external-${connection.externalMemberId}`,
      type: ConnectionType.EXTERNAL_MEMBER,
      label: getConnectionLabel(connection),
      externalMemberId: connection.externalMemberId,
    };
  }

  return null;
}

interface ShiftMemberAssignmentsEditorProps {
  assignees: DraftShiftMemberAssignee[];
  onChange: (assignees: DraftShiftMemberAssignee[]) => void;
  capacity?: number;
}

export default function ShiftMemberAssignmentsEditor({
  assignees,
  onChange,
  capacity,
}: ShiftMemberAssignmentsEditorProps): JSX.Element {
  const { t } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);

  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);

  const hasReachedCapacity =
    capacity !== undefined && Number.isInteger(capacity) && capacity > 0 && assignees.length >= capacity;

  const assigneeSuggestions = useMemo(() => {
    const query = assigneeSearch.trim().toLowerCase();

    return connections
      .filter((connection) => !!connection.active)
      .filter((connection) => {
        if (connection.type === ConnectionType.MEMBER) {
          return !!connection.userId;
        }

        return !!connection.externalMemberId;
      })
      .filter((connection) => {
        const selectedKey =
          connection.type === ConnectionType.MEMBER
            ? `member-${connection.userId}`
            : `external-${connection.externalMemberId}`;

        return !assignees.some((assignee) => assignee.key === selectedKey);
      })
      .filter((connection) => {
        if (!query) {
          return true;
        }

        return getConnectionLabel(connection).toLowerCase().includes(query);
      });
  }, [assigneeSearch, assignees, connections]);

  const loadConnections = async () => {
    try {
      setLoadingConnections(true);
      await connectionService.getConnections();
    } catch {
      toast.error(t('general.error'));
    } finally {
      setLoadingConnections(false);
    }
  };

  const addAssignee = (connection: Connection) => {
    if (hasReachedCapacity) {
      return;
    }

    const assignee = mapConnectionToAssignee(connection);
    if (!assignee) {
      return;
    }

    onChange([...assignees, assignee]);
    setAssigneeSearch('');
  };

  const removeAssignee = (key: string) => {
    onChange(assignees.filter((assignee) => assignee.key !== key));
  };

  const handleAssigneeInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (assigneeSearch.trim().length > 0 && assigneeSuggestions.length > 0 && !hasReachedCapacity) {
        event.preventDefault();
        addAssignee(assigneeSuggestions[0]);
      }
      return;
    }

    if (event.key === 'Backspace' && assigneeSearch.length === 0 && assignees.length > 0) {
      event.preventDefault();
      removeAssignee(assignees[assignees.length - 1].key);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-primary-900">{t('page.event.schedules.assignMembers')}</p>
          <p className="text-xs text-primary-500">
            {t('page.event.schedules.selectedAssignees', { count: assignees.length })}
          </p>
        </div>
        {hasReachedCapacity && (
          <span className="rounded-full bg-primary-900 px-2 py-1 text-xs text-secondary-50">
            {t('page.event.schedules.capacityReached')}
          </span>
        )}
      </div>

      <div className="relative">
        <div className="flex min-h-[48px] flex-wrap items-center rounded-xl border-2 border-secondary-200 bg-secondary-200 bg-opacity-50">
          {assignees.map((assignee) => (
            <span
              key={assignee.key}
              className="m-2 inline-flex items-center gap-2 rounded-full bg-secondary-200 px-3 py-1 text-sm text-primary-900">
              <span>{assignee.label}</span>
              <button
                type="button"
                onClick={() => removeAssignee(assignee.key)}
                className="text-primary-500 transition-colors hover:text-primary-900">
                <FaTimes className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            value={assigneeSearch}
            onChange={(event) => setAssigneeSearch(event.target.value)}
            onKeyDown={handleAssigneeInputKeyDown}
            onFocus={() => {
              setIsInputFocused(true);
              if (connections.length === 0) {
                loadConnections();
              }
            }}
            onBlur={() => {
              setTimeout(() => setIsInputFocused(false), 120);
            }}
            placeholder={t('page.event.schedules.searchMembersPlaceholder')}
            className="min-w-[180px] flex-1 border-none bg-transparent text-sm text-primary-900 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {!hasReachedCapacity && isInputFocused && (
          <div className="absolute z-10 mt-2 max-h-64 w-full overflow-auto rounded-xl bg-secondary-50 shadow-lg">
            {loadingConnections ? (
              <div className="px-3 py-2 text-sm text-primary-500">{t('general.loading')}</div>
            ) : assigneeSuggestions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-primary-500">
                {t('page.event.schedules.noMembersFound')}
              </div>
            ) : (
              assigneeSuggestions.slice(0, 5).map((connection) => (
                <button
                  key={
                    connection.type === ConnectionType.MEMBER
                      ? `member-${connection.userId}`
                      : `external-${connection.externalMemberId}`
                  }
                  type="button"
                  onClick={() => addAssignee(connection)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-primary-900 transition-colors hover:bg-secondary-200">
                  <span>{getConnectionLabel(connection)}</span>
                  <span className="text-xs text-primary-400">
                    {connection.type === ConnectionType.EXTERNAL_MEMBER ? 'External' : 'Member'}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function getMemberAssignmentsPayload(assignees: DraftShiftMemberAssignee[]): {
  participantsIds: number[];
  externalInvites: { externalMemberId: number }[];
} {
  return {
    participantsIds: assignees
      .filter((assignee) => assignee.type === ConnectionType.MEMBER && assignee.userId)
      .map((assignee) => assignee.userId as number),
    externalInvites: assignees
      .filter(
        (assignee) => assignee.type === ConnectionType.EXTERNAL_MEMBER && assignee.externalMemberId,
      )
      .map((assignee) => ({ externalMemberId: assignee.externalMemberId as number })),
  };
}
